import { useState, useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { UserTable } from '../../new/components/user-table';
import useUpdateMatter from '@/api/mutations/useUpdateMatter';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetUsersInOrg } from '@/api/queries/useGetUsersInOrg';
import { User } from '@/api/types/backend-dto-types';
import { useHasPermission } from '@/hooks/useHasPermission';

export function Users() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { hasPermission } = useHasPermission();
  const canAddUsers = hasPermission(Permission.MatterAddUsers);

  const { data: matter } = useGetMatterV2(matterId);
  const { data: users } = useGetUsersInOrg();

  const [addedUsers, setAddedUsers] = useState<User[]>([]);

  useEffect(() => {
    if (matter && matter.users) {
      setAddedUsers([...matter.users]);
    }
  }, [matter]);

  const { mutateAsync: updateMatter } = useUpdateMatter(matterId);

  const handleUserChange = async (users: User[]) => {
    setAddedUsers(users);
    try {
      await updateMatter({ userIds: users.map((x) => x.id) });
      toast.success('Users updated');
    } catch (error) {
      toast.error('Failed to update users');
    }
  };

  return (
    <>
      <SettingsHeader
        title="Users"
        subheading={
          <p className="text-sm text-gray-500">
            {canAddUsers
              ? 'View and manage the users who have access to this matter.'
              : 'View the users who have access to this matter.'}
          </p>
        }
      />

      {canAddUsers ? (
        <UserTable
          users={users ?? []}
          matterCreatorId={matter?.createdById}
          addedUsers={addedUsers}
          onUserChange={handleUserChange}
        />
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            You do not have permission to modify users. Contact an administrator to request access.
          </p>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Users with access ({addedUsers.length})</h3>
            <div className="space-y-2">
              {addedUsers.map((user) => (
                <div key={user.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="lowercase">{user.email}</span>
                  {user.id === matter?.createdById && <span className="text-xs text-gray-500">(Creator)</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
